import React, { FC, useEffect, useRef, useState } from 'react';

import { Box, HStack } from '@chakra-ui/react';
import { each, map, orderBy } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { EllipsisText, formatNumberWithComma } from 'components/DataDisplay';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import { CustomIconTypes, IconTypes } from 'components/Icons';
import { getIcon } from 'components/Icons/Components/getIcon';
import {
  renderSideNavHeader,
  SideNav,
  sideNavCategoryHeaderStyles,
  SideNavMenuItemProps,
} from 'components/Navigation/SideNav';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { Overview } from 'containers/Inventory/CloudInventory/Components/Overview';
import { InventorySummary } from 'containers/Inventory/CloudInventory/Components/Summary/InventorySummary';
import { useInventoryContext } from 'containers/Inventory/CloudInventory/hooks/context';
import { selectSharedState } from 'containers/SharedState/selectors';
import { useSearchParams } from 'hooks/useSearchParams';

import { selectInventory } from '../../selector';
import { actions } from '../../slice';
import { CategoryInventory } from '../CategoryInventory';
import { ResourceTypeInventory } from '../ResourceTypeInventory';

export const InventorySidebar: FC = () => {
  const { categories, currentCategory, instanceResourceType } = useSelector(
    selectInventory,
  );
  const { searchTerm } = useSelector(selectSharedState);
  const dispatch = useDispatch();
  const [items, setItems] = useState<SideNavMenuItemProps[]>([]);
  const { getCloudAgnosticName } = useResourceType();
  const params = useSearchParams<{
    resource_type: string;
    resource_category: string;
    resource_id: string;
    region: string;
  }>();
  const { updateValuesInURL } = useSearchParamFunctions();
  const lastSyncedSelectionRef = useRef<string>('');

  const {
    showInventorySummary,
    setShowInventorySummary,
    setShowCategoryInventorySummary,
    showCategoryInventorySummary,
    sideNavItemsMapping: itemsMapping,
    setSideNavItemsMapping: setItemsMapping,
    handleCategoryChange,
    updateMenuItems,
    setSelectedRegion,
  } = useInventoryContext();

  useEffect(() => {
    const getItems = (r, category) => {
      const title = getCloudAgnosticName(r?.name);
      const icon = getIcon(r?.category);
      return {
        data: r,
        title: (
          <HStack
            justify={'space-between'}
            w={'full'}
            pl={!r?.resource_types ? 8 : 0}
          >
            <CustomTooltip label={title}>
              <EllipsisText>{title}</EllipsisText>
            </CustomTooltip>
            <Box>
              {!r?.resource_types ? formatNumberWithComma(r?.total) : ''}
            </Box>
          </HStack>
        ),
        key: !r?.resource_types ? category + '_' + r?.name : r?.category,
        component: !!r?.resource_types ? (
          <CategoryInventory />
        ) : (
          <ResourceTypeInventory />
        ),
        ...(!!r?.resource_types
          ? {
              secondary: map(orderBy(r?.resource_types, ['name'], 'asc'), o =>
                getItems(o, r?.category),
              ),
              icon,
            }
          : {}),
      };
    };

    if (categories.data) {
      const items: SideNavMenuItemProps[] = map(
        orderBy(categories.data, ['category'], ['asc']),
        o => getItems(o, ''),
      );
      const orderedItems = items as SideNavMenuItemProps[];

      setItems([
        {
          title: renderSideNavHeader('Overview', IconTypes.DashboardOverview),
          key: 'Overview',
          data: { name: 'Overview' },
          component: <Overview />,
          showDivider: true,
        },
        {
          title: renderSideNavHeader(
            'By Categories',
            IconTypes.DashboardCategory,
          ),
          key: 'ByCategories',
          showArrow: false,
          styles: sideNavCategoryHeaderStyles,
          secondary: orderedItems,
        },
      ]);

      let mapping: Record<string, SideNavMenuItemProps> = {};
      each(orderedItems, o => {
        mapping[o.key] = o;
        each(o.secondary, s => (mapping[s.key] = s));
      });
      setItemsMapping(mapping);
    }
  }, [getCloudAgnosticName, categories.data, dispatch, setItemsMapping]);

  //if resource category is provided set sidenav current tab
  useEffect(() => {
    let category = items[0]?.key;
    let item = items[0];
    // Only switch to the URL category when it is known; otherwise keep the first category and its item.
    const urlItem = !!params.resource_category
      ? itemsMapping[params.resource_category]
      : undefined;
    if (!!urlItem) {
      item = urlItem;
      category = urlItem?.key;
    }
    const selectionKey = [
      category ?? '',
      params.resource_type ?? '',
      params.resource_id ?? '',
    ].join('|');

    if (lastSyncedSelectionRef.current === selectionKey) {
      return;
    }
    lastSyncedSelectionRef.current = selectionKey;

    if (category && category !== currentCategory) {
      handleCategoryChange(category as string, item);
    }
    if (
      !!params.resource_type &&
      params.resource_type !== instanceResourceType
    ) {
      dispatch(actions.setNativeResource(params.resource_type));
    }
    if (!!params.resource_id && params.resource_id !== searchTerm) {
      dispatch(actions.setSearchWord(params.resource_id));
    }
  }, [
    items,
    params.resource_category,
    params.resource_type,
    params.resource_id,
    itemsMapping,
    currentCategory,
    instanceResourceType,
    searchTerm,
    handleCategoryChange,
    dispatch,
  ]);

  useEffect(() => {
    updateMenuItems(items);
  }, [items, updateMenuItems]);

  useEffect(() => {
    if (!!params.region)
      setSelectedRegion({ label: params.region, value: params.region });
  }, [setSelectedRegion, params.region]);

  return (
    <Box h="full">
      <SideNav
        menuItems={items}
        isLoading={categories.isLoading}
        showContentHeader={false}
        onChange={(key, menuItem) => {
          handleCategoryChange(key, menuItem);
          updateValuesInURL({
            resource_category: key as string,
            resource_type: '',
            resource_id: undefined,
          });
        }}
        current={currentCategory}
        styles={{
          menu: { index: [1], defaultIndex: [] },
        }}
        leftAlignSecondaryItems
      />

      {showInventorySummary && (
        <InventorySummary
          type={CustomIconTypes.CloudInventory}
          isOpen={showInventorySummary}
          onClose={() => setShowInventorySummary(false)}
          header={CustomIconTypes.CloudInventory}
        />
      )}
      {showCategoryInventorySummary && (
        <InventorySummary
          type={currentCategory?.split('_')?.[0]}
          isOpen={showCategoryInventorySummary}
          onClose={() => setShowCategoryInventorySummary(false)}
          header={currentCategory?.split('_')?.[0]}
        />
      )}
    </Box>
  );
};
